import React from "react";
import styled from "styled-components";
import { 
  MessageSquare, 
  PenTool, 
  Code, 
  Rocket,
  ArrowRight,
  CheckCircle,
  Settings
} from "lucide-react";
import { 
  Container, 
  Section, 
  Card, 
  Badge
} from "../styles/GlobalStyles";

const HowItWorksSection = styled(Section)`
  background: var(--gray-50);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  
  .badge {
    margin-bottom: var(--space-4);
  }
  
  h2 {
    font-size: var(--text-5xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
    font-weight: 800;
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    color: var(--gray-600);
    max-width: 700px;
    margin: 0 auto;
    line-height: var(--leading-relaxed);
  }
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-10);
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-8);
  }
`;

const ProcessCard = styled(Card)`
  padding: var(--space-8);
  text-align: center;
  border: 2px solid transparent;
  background: var(--gray-0);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: var(--primary-200);
    transform: translateY(-8px);
    box-shadow: var(--shadow-2xl);
    
    .step-icon {
      transform: scale(1.1);
      background: var(--gradient-primary);
      color: var(--gray-0);
    }
    
    .step-number {
      background: var(--primary-600);
      color: var(--gray-0);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
    transform: scaleX(0);
    transition: transform var(--transition-normal);
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
  
  .step-number {
    position: absolute;
    top: var(--space-4);
    right: var(--space-4);
    width: 32px;
    height: 32px;
    background: var(--gray-100);
    color: var(--gray-600);
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-sm);
    font-weight: 700;
    transition: all var(--transition-normal);
  }
  
  .step-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto var(--space-6);
    background: var(--primary-50);
    border-radius: var(--radius-3xl);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-600);
    transition: all var(--transition-normal);
  }
  
  h3 {
    font-size: var(--text-2xl);
    color: var(--gray-900);
    margin-bottom: var(--space-4);
    font-weight: 700;
  }
  
  p {
    color: var(--gray-600);
    line-height: var(--leading-relaxed);
    margin-bottom: var(--space-6);
  }
  
  .features-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    text-align: left;
  }
  
  .feature-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    color: var(--gray-700);
    
    .check {
      color: var(--secondary-600);
      flex-shrink: 0;
    }
  }
`;

const TimelineSection = styled.div`
  display: flex;
  justify-content: center;
  margin-top: var(--space-16);
`;

const Timeline = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  background: var(--gray-0);
  padding: var(--space-6) var(--space-8);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--gray-200);
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: var(--space-6);
  }
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-2);
  }
  
  .timeline-icon {
    width: 40px;
    height: 40px;
    background: var(--gradient-primary);
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-0);
  }
  
  .timeline-text {
    font-size: var(--text-sm);
    color: var(--gray-700);
    font-weight: 600;
    
    .duration {
      color: var(--primary-600);
      font-weight: 700;
    }
  }
`;

const ArrowIcon = styled(ArrowRight)`
  color: var(--gray-400);
  
  @media (max-width: 768px) {
    transform: rotate(90deg);
  }
`;

const steps = [
  {
    number: "01",
    title: "Discovery & Analysis",
    description: "We start by understanding your business needs, analyzing requirements, and identifying the best technology stack for your project.",
    icon: <MessageSquare size={40} />,
    features: [
      "Requirements gathering",
      "Technology consultation", 
      "Project scope definition",
      "Timeline estimation"
    ]
  },
  {
    number: "02",
    title: "Design & Planning",
    description: "Our team creates detailed wireframes, UI/UX designs, and technical architecture to ensure a solid foundation.",
    icon: <PenTool size={40} />,
    features: [
      "UI/UX design",
      "System architecture",
      "Database design",
      "Project roadmap"
    ]
  },
  {
    number: "03",
    title: "Development & Testing",
    description: "We build your solution using agile methodology with continuous integration, testing, and quality assurance.",
    icon: <Code size={40} />,
    features: [
      "Agile development",
      "Code reviews",
      "Automated testing",
      "Quality assurance"
    ]
  },
  {
    number: "04",
    title: "Launch & Support",
    description: "We deploy your solution and provide ongoing maintenance, updates, and technical support to ensure optimal performance.",
    icon: <Rocket size={40} />,
    features: [
      "Deployment & launch",
      "Performance monitoring",
      "Ongoing support",
      "Regular updates"
    ]
  }
];

const timelineData = [
  { icon: <MessageSquare size={20} />, text: "Week 1-2", duration: "Discovery" },
  { icon: <PenTool size={20} />, text: "Week 3-4", duration: "Design" },
  { icon: <Code size={20} />, text: "Week 5-12", duration: "Development" },
  { icon: <Rocket size={20} />, text: "Week 13+", duration: "Launch & Support" }
];

// Technology Section Styled Components
const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-8);
  margin-top: var(--space-12);
`;

const TechCategory = styled.div`
  background: var(--gray-0);
  padding: var(--space-6);
  border-radius: var(--radius-xl);
  border: 1px solid var(--gray-200);
  transition: all var(--transition-fast);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: var(--primary-300);
  }
`;

const TechCategoryTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: var(--space-4);
  padding-bottom: var(--space-2);
  border-bottom: 2px solid var(--gradient-primary);
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const TechList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TechItem = styled.li`
  padding: var(--space-2) 0;
  color: var(--gray-700);
  font-size: var(--text-sm);
  position: relative;
  
  &:before {
    content: '•';
    color: var(--primary-600);
    font-size: var(--text-lg);
    margin-right: var(--space-2);
  }
  
  &:hover {
    color: var(--primary-600);
    transform: translateX(4px);
    transition: all var(--transition-fast);
  }
`;

const HowItWorks = () => {
  return (
    <HowItWorksSection>
      <Container>
        <SectionHeader>
          <Badge className="badge primary">
            <Settings size={12} />
            Our Process
          </Badge>
          <h2>How We Transform Your Ideas Into Reality</h2>
          <p className="subtitle">
            Our proven 4-step process ensures your software project is delivered 
            on time, within budget, and exceeds your expectations.
          </p>
        </SectionHeader>

        <ProcessGrid>
          {steps.map((step, index) => (
            <ProcessCard key={index}>
              <div className="step-number">{step.number}</div>
              
              <div className="step-icon">
                {step.icon}
              </div>
              
              <h3>{step.title}</h3>
              <p>{step.description}</p>
              
              <div className="features-list">
                {step.features.map((feature, idx) => (
                  <div key={idx} className="feature-item">
                    <CheckCircle size={16} className="check" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </ProcessCard>
          ))}
        </ProcessGrid>

        <TimelineSection>
          <Timeline>
            {timelineData.map((item, index) => (
              <React.Fragment key={index}>
                <TimelineItem>
                  <div className="timeline-icon">
                    {item.icon}
                  </div>
                  <div className="timeline-text">
                    <span className="duration">{item.duration}</span><br />
                    {item.text}
                  </div>
                </TimelineItem>
                {index < timelineData.length - 1 && <ArrowIcon size={20} />}
              </React.Fragment>
            ))}
          </Timeline>
        </TimelineSection>

        {/* Technologies Section */}
        <SectionHeader style={{ marginTop: 'var(--space-20)' }}>
          <Badge className="badge secondary">
            <Code size={12} />
            Our Tech Stack
          </Badge>
          <h2>Technologies We Master</h2>
          <p className="subtitle">
            We use modern, industry-standard technologies to build scalable, 
            secure, and maintainable software solutions.
          </p>
        </SectionHeader>

        <TechGrid>
          <TechCategory>
            <TechCategoryTitle>Frontend</TechCategoryTitle>
            <TechList>
              <TechItem>React.js</TechItem>
              <TechItem>Next.js</TechItem>
              <TechItem>JavaScript/TypeScript</TechItem>
              <TechItem>CSS3/SCSS</TechItem>
              <TechItem>Styled Components</TechItem>
            </TechList>
          </TechCategory>

          <TechCategory>
            <TechCategoryTitle>Backend</TechCategoryTitle>
            <TechList>
              <TechItem>Node.js</TechItem>
              <TechItem>Express.js</TechItem>
              <TechItem>.NET Core</TechItem>
              <TechItem>C#</TechItem>
              <TechItem>Python</TechItem>
              <TechItem>RESTful APIs</TechItem>
              <TechItem>Authentication</TechItem>
            </TechList>
          </TechCategory>

          <TechCategory>
            <TechCategoryTitle>Database</TechCategoryTitle>
            <TechList>
              <TechItem>MySQL</TechItem>
              <TechItem>PostgreSQL</TechItem>
              <TechItem>MongoDB</TechItem>
              <TechItem>Firebase</TechItem>
              <TechItem>Database Design</TechItem>
            </TechList>
          </TechCategory>

          <TechCategory>
            <TechCategoryTitle>Tools & Cloud</TechCategoryTitle>
            <TechList>
              <TechItem>Git/GitHub</TechItem>
              <TechItem>AWS/GCP</TechItem>
              <TechItem>Docker</TechItem>
              <TechItem>VS Code</TechItem>
              <TechItem>Figma</TechItem>
            </TechList>
          </TechCategory>
        </TechGrid>
      </Container>
    </HowItWorksSection>
  );
};

export default HowItWorks;
